import {
  BookOpen,
  ChevronRight,
  ChevronsUpDown,
  Copy,
  Database,
  Plus,
  Trash2,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { MarkdownEditor } from "../components/MarkdownEditor";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { useAuth } from "../hooks/useAuth";
import { apiRequest } from "../lib/api";
import { APP_ROUTES } from "../routes";
import type { AgentRecord, KnowledgeBaseRecord } from "../types";

const DEFAULT_INSTRUCTIONS =
  "You are a data and knowledge agent. Use the available tools to inspect approved tables, search approved knowledgebases, and answer with concise, grounded results.";

const createBlankAgentDraft = (): Partial<AgentRecord> => ({
  name: "",
  description: "",
  instructions: DEFAULT_INSTRUCTIONS,
  model: "gpt-5.6-luna",
  postgresConfig: {
    provider: "postgres",
    connectionString: "",
    tables: [],
    roleAccess: {
      admin: [],
      member: [],
    },
  },
  knowledgeBaseIds: [],
});

type KnowledgeBaseSummary = KnowledgeBaseRecord & {
  sourceCount?: number;
  chunkCount?: number;
};

type KnowledgeBaseListResponse = {
  knowledgeBases: KnowledgeBaseSummary[];
};

const uniqueIds = (ids: string[]): string[] =>
  Array.from(new Set(ids.filter((id) => Boolean(id.trim()))));

export function AgentsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [agents, setAgents] = useState<AgentRecord[]>([]);
  const [knowledgeBases, setKnowledgeBases] = useState<KnowledgeBaseSummary[]>([]);
  const [selectedAgentId, setSelectedAgentId] = useState("");
  const [draft, setDraft] = useState<Partial<AgentRecord>>(createBlankAgentDraft());
  const [loading, setLoading] = useState(true);
  const [knowledgeBasesLoading, setKnowledgeBasesLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectorOpen, setSelectorOpen] = useState(false);
  const [integrationsOpen, setIntegrationsOpen] = useState(true);
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const selectorRef = useRef<HTMLDivElement | null>(null);

  const loadKnowledgeBases = async () => {
    if (!user?.activeOrganizationId) {
      setKnowledgeBases([]);
      return;
    }

    setKnowledgeBasesLoading(true);
    try {
      const payload = await apiRequest<KnowledgeBaseListResponse>("/api/knowledgebases");
      setKnowledgeBases(payload.knowledgeBases ?? []);
    } catch (loadError) {
      setError(
        loadError instanceof Error
          ? loadError.message
          : "Failed to load knowledgebases",
      );
    } finally {
      setKnowledgeBasesLoading(false);
    }
  };

  const loadAgents = async () => {
    if (!user?.activeOrganizationId) {
      setAgents([]);
      setSelectedAgentId("");
      setDraft(createBlankAgentDraft());
      setError(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const nextAgents = await apiRequest<AgentRecord[]>("/auth/agents");
      setAgents(nextAgents);

      const preferredAgentId = searchParams.get("agentId");
      const nextSelectedId =
        nextAgents.find((agent) => agent.id === preferredAgentId)?.id ??
        nextAgents.find((agent) => agent.id === selectedAgentId)?.id ??
        nextAgents[0]?.id ??
        "";
      setSelectedAgentId(nextSelectedId);
    } catch (loadError) {
      setError(
        loadError instanceof Error ? loadError.message : "Failed to load agents",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadAgents();
  }, [user?.activeOrganizationId]);

  useEffect(() => {
    void loadKnowledgeBases();
  }, [user?.activeOrganizationId]);

  useEffect(() => {
    const handlePointerDown = (event: MouseEvent) => {
      if (!selectorRef.current?.contains(event.target as Node)) {
        setSelectorOpen(false);
      }
    };

    window.addEventListener("mousedown", handlePointerDown);
    return () => window.removeEventListener("mousedown", handlePointerDown);
  }, []);

  const selectedAgent = useMemo(
    () => agents.find((agent) => agent.id === selectedAgentId) ?? null,
    [agents, selectedAgentId],
  );
  const attachedKnowledgeBaseIds = useMemo(
    () => uniqueIds(draft.knowledgeBaseIds ?? []),
    [draft.knowledgeBaseIds],
  );
  const attachedKnowledgeBaseIdSet = useMemo(
    () => new Set(attachedKnowledgeBaseIds),
    [attachedKnowledgeBaseIds],
  );
  const attachedKnowledgeBases = useMemo(
    () =>
      attachedKnowledgeBaseIds.map(
        (id) =>
          knowledgeBases.find((knowledgeBase) => knowledgeBase.id === id) ?? {
            id,
            organizationId: user?.activeOrganizationId ?? "",
            name: "Unknown knowledgebase",
            description: null,
            createdAt: "",
            updatedAt: "",
          },
      ),
    [attachedKnowledgeBaseIds, knowledgeBases, user?.activeOrganizationId],
  );
  const availableKnowledgeBases = useMemo(
    () =>
      knowledgeBases.filter(
        (knowledgeBase) => !attachedKnowledgeBaseIdSet.has(knowledgeBase.id),
      ),
    [attachedKnowledgeBaseIdSet, knowledgeBases],
  );

  useEffect(() => {
    const nextParams = new URLSearchParams(searchParams);
    if (selectedAgentId) {
      nextParams.set("agentId", selectedAgentId);
    } else {
      nextParams.delete("agentId");
    }
    setSearchParams(nextParams, { replace: true });
  }, [selectedAgentId]);

  useEffect(() => {
    if (!selectedAgent) {
      setDraft(createBlankAgentDraft());
      return;
    }

    setDraft(JSON.parse(JSON.stringify(selectedAgent)) as AgentRecord);
    setError(null);
    setStatus(null);
  }, [selectedAgent]);

  const saveDraft = async (nextDraft: Partial<AgentRecord>) => {
    setSaving(true);
    setError(null);

    try {
      const saved = await apiRequest<AgentRecord>("/auth/saveAgent", {
        method: "POST",
        body: JSON.stringify({
          agent: {
            id: nextDraft.id,
            name: nextDraft.name?.trim() || "New Database Agent",
            description: nextDraft.description ?? "",
            instructions: nextDraft.instructions?.trim() || DEFAULT_INSTRUCTIONS,
            model: nextDraft.model ?? "gpt-5.6-luna",
            knowledgeBaseIds: nextDraft.knowledgeBaseIds ?? [],
            postgresConfig: nextDraft.postgresConfig ?? {
              provider: "postgres",
              connectionString: "",
              tables: [],
              roleAccess: {
                admin: [],
                member: [],
              },
            },
          },
        }),
      });

      setAgents((current) => {
        const exists = current.some((agent) => agent.id === saved.id);
        if (!exists) {
          return [saved, ...current];
        }
        return current.map((agent) => (agent.id === saved.id ? saved : agent));
      });
      setSelectedAgentId(saved.id);
      setDraft(JSON.parse(JSON.stringify(saved)) as AgentRecord);
      setStatus("Saved");
    } catch (saveError) {
      setError(
        saveError instanceof Error ? saveError.message : "Failed to save agent",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleCreateAgent = async () => {
    const created = await apiRequest<AgentRecord>("/auth/newAgent", {
      method: "POST",
    });
    await loadAgents();
    setSelectedAgentId(created.id);
    setSelectorOpen(false);
  };

  const handleCloneAgent = async () => {
    if (!selectedAgent) {
      return;
    }

    const cloned = await apiRequest<AgentRecord>("/auth/saveAgent", {
      method: "POST",
      body: JSON.stringify({
        agent: {
          name: `Copy of ${draft.name || selectedAgent.name}`,
          description: draft.description ?? "",
          instructions: draft.instructions ?? DEFAULT_INSTRUCTIONS,
          model: draft.model ?? "gpt-5.6-luna",
          knowledgeBaseIds: draft.knowledgeBaseIds ?? [],
          postgresConfig: draft.postgresConfig ?? {
            provider: "postgres",
            connectionString: "",
            tables: [],
            roleAccess: {
              admin: [],
              member: [],
            },
          },
        },
      }),
    });

    await loadAgents();
    setSelectedAgentId(cloned.id);
  };

  const handleDeleteAgent = async () => {
    if (!draft.id) {
      return;
    }

    const confirmed = window.confirm("Delete this agent?");
    if (!confirmed) {
      return;
    }

    await apiRequest<{ success: boolean }>("/auth/deleteAgent", {
      method: "POST",
      body: JSON.stringify({ agent: { id: draft.id } }),
    });

    await loadAgents();
  };

  const updateDraftField = async (
    key: "name" | "instructions",
    value: string,
  ) => {
    const nextDraft = { ...draft, [key]: value };
    setDraft(nextDraft);
    await saveDraft(nextDraft);
  };

  const saveDraftKnowledgeBaseIds = async (knowledgeBaseIds: string[]) => {
    const nextDraft = {
      ...draft,
      knowledgeBaseIds: uniqueIds(knowledgeBaseIds),
    };
    setDraft(nextDraft);
    await saveDraft(nextDraft);
  };

  if (!selectedAgent && !loading && agents.length === 0) {
    return (
      <div className="flex min-h-full flex-col p-4 px-6 pb-10 md:px-16">
        <div className="pt-4 md:pt-6">
          <h2 className="text-2xl font-semibold tracking-tight text-gray-900">
            Agents
          </h2>
          <p className="mt-1 text-sm text-slate-500">
            Configure agents that power the AI chat experience.
          </p>
        </div>
        <div className="mt-8">
          <button
            onClick={() => void handleCreateAgent()}
            className="inline-flex items-center gap-2 rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-900 transition hover:bg-slate-50"
          >
            <Plus className="h-4 w-4 text-blue-500" />
            Create New Agent
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-full flex-col p-4 px-6 pb-10 md:px-16">
      <div className="flex flex-col gap-4 pt-4 md:flex-row md:items-center md:justify-between md:pt-6">
        <div>
          <h2 className="text-2xl font-semibold tracking-tight text-gray-900">
            Agents
          </h2>
          <p className="mt-1 text-sm text-slate-500">
            Configure agents that power the AI chat experience.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <div className="relative" ref={selectorRef}>
            <button
              onClick={() => setSelectorOpen((current) => !current)}
              className="inline-flex min-w-[180px] items-center justify-between rounded-md border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-900"
            >
              <span className="truncate">
                {selectedAgent?.name ?? (loading ? "Loading..." : "Select an Agent")}
              </span>
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </button>

            {selectorOpen ? (
              <div className="absolute right-0 z-30 mt-2 max-h-[300px] w-[250px] overflow-y-auto rounded-md border border-slate-200 bg-white shadow-lg">
                <button
                  onClick={() => void handleCreateAgent()}
                  className="flex w-full items-center gap-2 border-b border-slate-100 px-3 py-2 text-left text-sm font-medium text-slate-900 hover:bg-slate-50"
                >
                  <Plus className="h-4 w-4 text-blue-500" />
                  Create New Agent
                </button>
                {agents
                  .slice()
                  .sort((left, right) => left.name.localeCompare(right.name))
                  .map((agent) => (
                    <button
                      key={agent.id}
                      onClick={() => {
                        setSelectedAgentId(agent.id);
                        setSelectorOpen(false);
                      }}
                      className="block w-full truncate px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50"
                    >
                      {agent.name}
                    </button>
                  ))}
              </div>
            ) : null}
          </div>

          <button
            onClick={() => void handleCloneAgent()}
            title="Clone Agent"
            className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-slate-200 bg-white"
          >
            <Copy className="h-4 w-4" />
          </button>
          <button
            onClick={() => void handleDeleteAgent()}
            title="Delete Agent"
            className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-slate-200 bg-white text-red-600 hover:bg-red-50"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="my-4 border-t border-slate-200" />

      {error ? (
        <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      <div className="space-y-6">
        <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,520px)_minmax(0,1fr)]">
          <section className="rounded-lg border border-slate-200 bg-white">
            <div className="border-b border-slate-200 px-6 py-4">
              <h3 className="text-base font-semibold text-slate-900">
                Configuration
              </h3>
            </div>

            <div className="px-6 py-5">
              <div className="mb-6">
                <label className="mb-2 block text-sm font-medium text-slate-700">
                  Name
                </label>
                <input
                  value={draft.name ?? ""}
                  onChange={(event) => setDraft({ ...draft, name: event.target.value })}
                  onBlur={() => void updateDraftField("name", draft.name ?? "")}
                  placeholder="Enter agent name"
                  className="w-full rounded-md border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-blue-300 focus:ring-1 focus:ring-blue-200"
                />
              </div>

              <div className="mb-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-slate-700">Functions</p>
                  </div>
                </div>
                <p className="mt-3 text-sm text-slate-500">
                  No custom functions are configured in this workspace.
                </p>
              </div>

              <div className="border-t border-slate-200 py-6">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex min-w-0 items-center gap-3">
                    <BookOpen className="h-5 w-5 shrink-0 text-slate-500" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-700">Knowledgebase</p>
                      <p className="mt-0.5 text-xs text-slate-500">
                        {attachedKnowledgeBaseIds.length === 0
                          ? "No libraries attached"
                          : `${attachedKnowledgeBaseIds.length} attached`}
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => navigate(APP_ROUTES.knowledgebase)}
                    className="text-xs font-medium text-cyan-700 hover:text-cyan-800"
                  >
                    Manage
                  </button>
                </div>

                <div className="mt-4 space-y-2">
                  <Select
                    value=""
                    onValueChange={(knowledgeBaseId) => {
                      if (!knowledgeBaseId) {
                        return;
                      }
                      void saveDraftKnowledgeBaseIds([
                        ...attachedKnowledgeBaseIds,
                        knowledgeBaseId,
                      ]);
                    }}
                    disabled={
                      saving ||
                      knowledgeBasesLoading ||
                      availableKnowledgeBases.length === 0
                    }
                  >
                    <SelectTrigger>
                      <SelectValue
                        placeholder={
                          knowledgeBasesLoading
                            ? "Loading libraries..."
                            : availableKnowledgeBases.length === 0
                              ? "No more libraries to attach"
                              : "Attach a library"
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {availableKnowledgeBases.map((knowledgeBase) => (
                        <SelectItem key={knowledgeBase.id} value={knowledgeBase.id}>
                          {knowledgeBase.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {attachedKnowledgeBases.length > 0 ? (
                    <div className="space-y-2">
                      {attachedKnowledgeBases.map((knowledgeBase) => (
                        <div
                          key={knowledgeBase.id}
                          className="flex items-center justify-between gap-3 rounded-md border border-slate-100 bg-slate-50 px-3 py-2"
                        >
                          <div className="min-w-0">
                            <p className="truncate text-sm font-medium text-slate-800">
                              {knowledgeBase.name}
                            </p>
                            <p className="mt-0.5 text-xs text-slate-500">
                              {knowledgeBase.sourceCount ?? 0} sources,
                              {" "}
                              {knowledgeBase.chunkCount ?? 0} chunks
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() =>
                              void saveDraftKnowledgeBaseIds(
                                attachedKnowledgeBaseIds.filter(
                                  (id) => id !== knowledgeBase.id,
                                ),
                              )
                            }
                            disabled={saving}
                            className="shrink-0 text-xs font-medium text-slate-500 hover:text-red-600 disabled:opacity-50"
                          >
                            Remove
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : null}
                </div>
              </div>

              <div className="border-t border-slate-200 pt-6">
                <button
                  onClick={() => setIntegrationsOpen((current) => !current)}
                  className="flex w-full items-center justify-between"
                >
                  <p className="text-sm font-medium text-slate-700">Integrations</p>
                  <ChevronRight
                    className={`h-4 w-4 text-slate-500 transition-transform ${
                      integrationsOpen ? "rotate-90" : ""
                    }`}
                  />
                </button>

                {integrationsOpen ? (
                  <div className="mt-3">
                    <button
                      onClick={() =>
                        navigate(`${APP_ROUTES.agents}/${selectedAgentId}/integrations/database`)
                      }
                      className="flex w-full items-center justify-between rounded-sm border-b border-slate-100 px-1 py-3 text-left transition hover:bg-slate-50"
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-lg text-slate-500">
                          <Database className="h-5 w-5" />
                        </span>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-slate-800">
                              {draft.postgresConfig?.provider === "mysql" ? "MySQL" : "Database"}
                            </span>
                            {draft.postgresConfig?.connectionString ? (
                              <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs text-green-800">
                                Enabled
                              </span>
                            ) : null}
                          </div>
                          <p className="mt-0.5 text-sm text-slate-500">
                            Connect a PostgreSQL or MySQL database to use as a data source.
                          </p>
                        </div>
                      </div>
                      <ChevronRight className="h-4 w-4 text-slate-500" />
                    </button>
                  </div>
                ) : null}
              </div>
            </div>
          </section>

          <section className="rounded-lg border border-slate-200 bg-white">
            <div className="border-b border-slate-200 px-6 py-4">
              <h3 className="text-base font-semibold text-slate-900">Prompt</h3>
            </div>

            <div className="px-6 py-5">
              <MarkdownEditor
                value={draft.instructions ?? ""}
                onChange={(value) =>
                  setDraft({ ...draft, instructions: value })
                }
                onSave={() =>
                  void updateDraftField("instructions", draft.instructions ?? "")
                }
                placeholder="Enter your markdown content here..."
              />
            </div>
          </section>
        </div>
      </div>

      {saving || status ? (
        <div className="mt-4 text-sm text-slate-500">
          {saving ? "Saving..." : status}
        </div>
      ) : null}
    </div>
  );
}
